import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { dbHelpers, storageHelpers } from '../../../lib/supabase';

export default function NewSpace() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    spaceType: '',
    location: '',
    size: '',
    price: '',
    description: '',
    features: {
      security: false,
      cctv: false,
      lighting: false,
      covered: false,
      accessible: false,
      electric: false
    }
  });

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const totalImages = imageFiles.length + newFiles.length;

    if (totalImages > 5) {
      alert('En fazla 5 görsel yükleyebilirsiniz.');
      return;
    }

    // Validate file types and sizes
    const validFiles = newFiles.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB

      if (!isValidType) {
        alert(`${file.name} geçerli bir görsel dosyası değil.`);
        return false;
      }
      if (!isValidSize) {
        alert(`${file.name} çok büyük. Maksimum 5MB olmalıdır.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Create previews
    const newPreviews: string[] = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result as string);
        if (newPreviews.length === validFiles.length) {
          setImagePreviews([...imagePreviews, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setImageFiles([...imageFiles, ...validFiles]);
  };

  const handleRemoveImage = (index: number) => {
    const newFiles = imageFiles.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setImageFiles(newFiles);
    setImagePreviews(newPreviews);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Upload images first
      let imageUrls: string[] = [];
      if (imageFiles.length > 0) {
        setUploadingImages(true);
        const { data, error: uploadError } = await storageHelpers.uploadMultipleImages(imageFiles);
        setUploadingImages(false);

        if (uploadError) {
          alert('Görseller yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
          setLoading(false);
          return;
        }

        imageUrls = data || [];
      }

      // Create space with images
      const { data, error } = await dbHelpers.createSpace({
        space_type: formData.spaceType,
        location: formData.location,
        size: formData.size,
        price: formData.price,
        description: formData.description,
        images: imageUrls,
      });

      if (error) {
        alert('Bir hata oluştu. Lütfen tekrar deneyin.');
      } else {
        alert('Alanınız başarıyla kaydedildi!');
        navigate('/spaces');
      }
    } catch (error) {
      alert('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFeatureChange = (feature: string) => {
    setFormData({
      ...formData,
      features: {
        ...formData.features,
        [feature]: !formData.features[feature as keyof typeof formData.features]
      }
    });
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/10 backdrop-blur-md shadow-sm sticky top-0 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-white cursor-pointer">
                pad<span className="text-orange-500">baq</span>
              </Link>
            </div>
            <nav className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Panel</Link>
              <Link to="/spaces" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Alanlarım</Link>
              <Link to="/boxes" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Koli Yönetimi</Link>
              <Link to="/profile" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Profil</Link>
              <button 
                onClick={handleLogout}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-circle-line mr-2"></i>
                Çıkış
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Link to="/spaces" className="text-white/60 hover:text-white cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>
              Alanlarım
            </Link>
            <span className="text-white/40 mx-2">/</span>
            <span className="text-orange-400">Yeni Alan Ekle</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Yeni Alan Ekle</h1>
          <p className="text-white/70">Boş alanınızı kiralayarak gelir elde edin</p>
        </div>

        {/* Form */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10">
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Temel Bilgiler */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <i className="ri-information-line mr-2 text-orange-400"></i>
                  Temel Bilgiler
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Alan Türü <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <select 
                        name="spaceType"
                        value={formData.spaceType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white text-sm pr-8"
                      >
                        <option value="" className="bg-slate-800">Seçiniz</option>
                        <option value="otopark" className="bg-slate-800">Otopark</option>
                        <option value="depo" className="bg-slate-800">Depo/Ambar</option>
                        <option value="garaj" className="bg-slate-800">Garaj</option>
                        <option value="bahce" className="bg-slate-800">Bahçe</option>
                        <option value="diger" className="bg-slate-800">Diğer</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 pointer-events-none"></i>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Alan Boyutu <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="text"
                      name="size"
                      value={formData.size}
                      onChange={handleInputChange}
                      placeholder="Örn: 20 m², 1 araçlık, 50 m³"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Konum <span className="text-red-400">*</span>
                    </label>
                    <input 
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="İl, İlçe, Mahalle, Sokak (Detaylı adres girin)"
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/90 mb-2">
                      Aylık Kira Bedeli <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input 
                        type="text"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        placeholder="Örn: 1500"
                        required
                        className="w-full px-4 py-3 pr-12 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm"
                      />
                      <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/70 text-sm">₺</span>
                    </div>
                    <p className="text-xs text-white/50 mt-1">Sadece rakam girin, ₺ simgesi otomatik eklenecek</p>
                  </div>
                </div>
              </div>

              {/* Görsel Yükleme */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <i className="ri-image-line mr-2 text-orange-400"></i>
                  Görsel Ekle
                </h3>
                
                <div className="space-y-4">
                  <p className="text-sm text-white/70">
                    Alanınızın fotoğraflarını ekleyin (En fazla 5 adet, her biri max 5MB)
                  </p>

                  {/* Image Upload Area */}
                  {imagePreviews.length < 5 && (
                    <div className="relative">
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        multiple
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                      <label
                        htmlFor="image-upload"
                        className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/30 rounded-lg hover:border-orange-500 transition-colors cursor-pointer bg-white/5 hover:bg-white/10"
                      >
                        <i className="ri-upload-cloud-line text-4xl text-white/50 mb-2"></i>
                        <span className="text-white/70 text-sm">Görsel yüklemek için tıklayın</span>
                        <span className="text-white/50 text-xs mt-1">
                          {imagePreviews.length}/5 görsel yüklendi
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Image Previews */}
                  {imagePreviews.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Önizleme ${index + 1}`}
                            className="w-full h-40 object-cover rounded-lg border border-white/20"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index)}
                            className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                          {index === 0 && (
                            <div className="absolute bottom-2 left-2 px-2 py-1 bg-orange-500 text-white text-xs rounded-full">
                              Ana Görsel
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-xs text-blue-200 flex items-start">
                      <i className="ri-information-line mr-2 mt-0.5 flex-shrink-0"></i>
                      <span>İlk yüklediğiniz görsel ana görsel olarak kullanılacaktır. Görseller alanınızın daha hızlı kiralanmasına yardımcı olur.</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Özellikler */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <i className="ri-star-line mr-2 text-orange-400"></i>
                  Özellikler
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries({
                    security: { icon: 'ri-shield-check-line', label: 'Güvenlik' },
                    cctv: { icon: 'ri-camera-line', label: 'CCTV' },
                    lighting: { icon: 'ri-lightbulb-line', label: 'Aydınlatma' },
                    covered: { icon: 'ri-home-4-line', label: 'Kapalı Alan' },
                    accessible: { icon: 'ri-wheelchair-line', label: 'Engelli Erişimi' },
                    electric: { icon: 'ri-flashlight-line', label: 'Elektrik' }
                  }).map(([key, { icon, label }]) => (
                    <div key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        id={key}
                        checked={formData.features[key as keyof typeof formData.features]}
                        onChange={() => handleFeatureChange(key)}
                        className="w-4 h-4 text-orange-600 bg-white/10 border-white/30 rounded focus:ring-orange-500 focus:ring-2 cursor-pointer"
                      />
                      <label htmlFor={key} className="ml-3 flex items-center text-sm text-white cursor-pointer">
                        <i className={`${icon} mr-2 text-orange-400`}></i>
                        {label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Açıklama */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                  <i className="ri-file-text-line mr-2 text-orange-400"></i>
                  Detaylı Açıklama
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Alanınız Hakkında Detaylar
                  </label>
                  <textarea 
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Alanınız hakkında detaylı bilgi verin... (Erişim bilgileri, güvenlik özellikleri, yakın konumlar, özel durumlar vb.)"
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white placeholder-white/50 text-sm resize-none"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-white/50">Detaylı açıklama ile daha fazla kiracı çekersiniz</p>
                    <span className="text-xs text-white/50">{formData.description.length}/500 karakter</span>
                  </div>
                </div>
              </div>

              {/* Bilgilendirme */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-white font-medium mb-2 flex items-center">
                  <i className="ri-information-line mr-2 text-blue-400"></i>
                  İlan Yayınlandıktan Sonra
                </h4>
                <ul className="text-sm text-blue-200 space-y-1">
                  <li>• İlanınız anında yayına girecek</li>
                  <li>• Kiracılar sizinle doğrudan iletişime geçebilecek</li>
                  <li>• İlan detaylarını istediğiniz zaman güncelleyebilirsiniz</li>
                  <li>• Kira sözleşmesi ve ödeme süreçleri padbaq güvencesiyle gerçekleşir</li>
                </ul>
              </div>

              {/* Form Actions */}
              <div className="flex flex-col md:flex-row gap-4 pt-6 border-t border-white/10">
                <Link
                  to="/spaces"
                  className="flex-1 px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors text-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Geri Dön
                </Link>
                <button
                  type="submit"
                  disabled={loading || uploadingImages || !formData.spaceType || !formData.location || !formData.size || !formData.price}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingImages ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      Görseller Yükleniyor...
                    </>
                  ) : loading ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      İlan Yayınlanıyor...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      İlanı Yayınla
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* Chat Support Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white w-14 h-14 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer">
          <i className="ri-chat-3-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}